"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.deleteModulationApiResponseSchema = exports.initializeModulationsApiResponseSchema = exports.skippedBaselineItemSchema = exports.initializedBaselineItemSchema = exports.listAssistantModulationsApiResponseSchema = exports.modulationListItemSchema = exports.createModulationsApiResponseSchema = exports.previewModulationsApiResponseSchema = exports.modulationPreviewItemSchema = exports.modulationBlockedReasonSchema = void 0;
const zod_1 = require("zod");
const isoWeekSchema = zod_1.z.string().regex(/^\d{4}-W\d{2}$/);
/**
 * Why an assistant cannot be modulated over the requested period.
 *
 * - `already_modulated`: some contract week is covered by an existing record.
 * - `missing_planning`: some contract week holds no planned slot at all.
 */
exports.modulationBlockedReasonSchema = zod_1.z.enum([
    'already_modulated',
    'missing_planning',
]);
/** Per-assistant outcome of a modulation preview/creation. */
exports.modulationPreviewItemSchema = zod_1.z.object({
    assistantId: zod_1.z.string().uuid(),
    firstname: zod_1.z.string(),
    lastname: zod_1.z.string(),
    weeklyContractHours: zod_1.z.number(),
    /** Weeks of the period this assistant was actually under contract for. */
    includedIsoWeeks: zod_1.z.array(isoWeekSchema),
    numberOfWeeks: zod_1.z.number().int().min(0),
    contractMinutes: zod_1.z.number().int(),
    workedMinutes: zod_1.z.number().int(),
    actualWorkedMinutes: zod_1.z.number().int(),
    paidLeaveMinutes: zod_1.z.number().int(),
    publicHolidayMinutes: zod_1.z.number().int(),
    maternityMinutes: zod_1.z.number().int(),
    birthLeaveMinutes: zod_1.z.number().int(),
    sickMinutes: zod_1.z.number().int(),
    exceptionalMinutes: zod_1.z.number().int(),
    unpaidLeaveMinutes: zod_1.z.number().int(),
    startingBalanceMinutes: zod_1.z.number().int(),
    deltaMinutes: zod_1.z.number().int(),
    overtimeMinutes: zod_1.z.number().int(),
    carryOverMinutes: zod_1.z.number().int(),
    lockedWeeks: zod_1.z.array(isoWeekSchema),
    /** Contract weeks holding no planned slot at all (hours unknown, not zero). */
    weeksWithoutPlanning: zod_1.z.array(isoWeekSchema),
    /** Why creation is blocked, null when the period can be created. */
    blockedReason: exports.modulationBlockedReasonSchema.nullable(),
    blocked: zod_1.z.boolean(),
});
exports.previewModulationsApiResponseSchema = zod_1.z.object({
    startIsoWeek: isoWeekSchema,
    endIsoWeek: isoWeekSchema,
    includedIsoWeeks: zod_1.z.array(isoWeekSchema),
    scopeOfficeIds: zod_1.z.array(zod_1.z.string().uuid()),
    items: zod_1.z.array(exports.modulationPreviewItemSchema),
});
exports.createModulationsApiResponseSchema = zod_1.z.object({
    created: zod_1.z.number().int().min(0),
    skipped: zod_1.z.number().int().min(0),
    items: zod_1.z.array(exports.modulationPreviewItemSchema),
});
/** One persisted modulation period in an assistant's history (newest first). */
exports.modulationListItemSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    periodStartIsoWeek: isoWeekSchema,
    periodEndIsoWeek: isoWeekSchema,
    periodStartDate: zod_1.z.coerce.date(),
    periodEndDate: zod_1.z.coerce.date(),
    numberOfWeeks: zod_1.z.number().int().min(0),
    contractMinutes: zod_1.z.number().int(),
    workedMinutes: zod_1.z.number().int(),
    actualWorkedMinutes: zod_1.z.number().int().nullable(),
    paidLeaveMinutes: zod_1.z.number().int().nullable(),
    publicHolidayMinutes: zod_1.z.number().int().nullable(),
    maternityMinutes: zod_1.z.number().int().nullable(),
    birthLeaveMinutes: zod_1.z.number().int().nullable(),
    sickMinutes: zod_1.z.number().int().nullable(),
    exceptionalMinutes: zod_1.z.number().int().nullable(),
    unpaidLeaveMinutes: zod_1.z.number().int().nullable(),
    startingBalanceMinutes: zod_1.z.number().int(),
    deltaMinutes: zod_1.z.number().int(),
    overtimeMinutes: zod_1.z.number().int(),
    carryOverMinutes: zod_1.z.number().int(),
    createdAt: zod_1.z.coerce.date(),
});
exports.listAssistantModulationsApiResponseSchema = zod_1.z.object({
    assistantId: zod_1.z.string().uuid(),
    /** Running carried balance = latest period's carry-over (≤0); 0 when none. */
    currentBalanceMinutes: zod_1.z.number().int(),
    items: zod_1.z.array(exports.modulationListItemSchema),
});
/** One assistant a baseline was (or, in dry-run, would be) written for. */
exports.initializedBaselineItemSchema = zod_1.z.object({
    assistantId: zod_1.z.string().uuid(),
    firstname: zod_1.z.string(),
    lastname: zod_1.z.string(),
    weeklyContractHours: zod_1.z.number(),
    contractMinutes: zod_1.z.number().int(),
});
/** One assistant left untouched because a record already covers her weeks. */
exports.skippedBaselineItemSchema = zod_1.z.object({
    assistantId: zod_1.z.string().uuid(),
    firstname: zod_1.z.string(),
    lastname: zod_1.z.string(),
    conflictingIsoWeeks: zod_1.z.array(isoWeekSchema),
});
exports.initializeModulationsApiResponseSchema = zod_1.z.object({
    created: zod_1.z.number().int().min(0),
    skipped: zod_1.z.number().int().min(0),
    items: zod_1.z.array(exports.initializedBaselineItemSchema),
    skippedItems: zod_1.z.array(exports.skippedBaselineItemSchema),
});
exports.deleteModulationApiResponseSchema = zod_1.z.object({
    assistantId: zod_1.z.string().uuid(),
    periodStartIsoWeek: isoWeekSchema,
    periodEndIsoWeek: isoWeekSchema,
    /** The assistant's carried balance once the record is gone. */
    currentBalanceMinutes: zod_1.z.number().int(),
});
